/**
 * AI Filter Presets - One-tap filter bundles with ranking weight adjustments
 * Each preset includes:
 * - filters: Filter criteria to apply
 * - rankingWeights: Custom ranking weights for this intent
 * - enabled: Admin toggle (default: true)
 */

// Default ranking weights (used as base)
export const DEFAULT_RANKING_WEIGHTS = {
  relevance_score: 0.30,
  price_fairness_score: 0.20,
  trust_score: 0.15,
  listing_quality_score: 0.10,
  freshness_score: 0.10,
  distance_score: 0.10,
  seller_responsiveness_score: 0.05
};

export const AI_PRESETS = {
  cars: [
    {
      id: 'best_family_cars',
      name: 'Best Family Cars',
      description: 'Spacious, safe, and affordable to run',
      icon: 'Users',
      enabled: true,
      filters: {
        bodyType: ['suv', 'mpv', 'wagon'],
        runningCostBand: ['low', 'medium'],
        verifiedSeller: true,
        seats: ['5', '7', '8+']
      },
      rankingWeights: {
        relevance_score: 0.25,
        price_fairness_score: 0.15,      // Lower - families prioritize features over price
        trust_score: 0.25,               // Higher - safety matters
        listing_quality_score: 0.15,     // Higher - want detailed info
        freshness_score: 0.08,
        distance_score: 0.07,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'lowest_monthly_cost',
      name: 'Lowest Monthly Cost',
      description: 'Best value for budget-conscious buyers',
      icon: 'Wallet',
      enabled: true,
      filters: {
        runningCostBand: ['low'],
        insuranceEstimateBand: ['low'],
        mileageMax: 80000
      },
      rankingWeights: {
        relevance_score: 0.20,
        price_fairness_score: 0.35,      // Much higher - price is key
        trust_score: 0.15,
        listing_quality_score: 0.05,
        freshness_score: 0.15,           // Higher - want recent deals
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'city_friendly',
      name: 'City Friendly',
      description: 'Perfect for urban driving',
      icon: 'Building2',
      enabled: true,
      filters: {
        bodyType: ['hatchback'],
        fuelType: ['hybrid', 'electric'],
        ulezCompliant: true
      },
      countryEnable: ['UK'],
      rankingWeights: {
        relevance_score: 0.30,
        price_fairness_score: 0.20,
        trust_score: 0.15,
        listing_quality_score: 0.10,
        freshness_score: 0.10,
        distance_score: 0.10,            // Important for city buyers
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'first_car',
      name: 'First Car',
      description: 'Reliable and easy to insure',
      icon: 'GraduationCap',
      enabled: true,
      filters: {
        insuranceEstimateBand: ['low'],
        engineSize: { max: 1400 },
        mileageMax: 60000
      },
      rankingWeights: {
        relevance_score: 0.20,
        price_fairness_score: 0.25,      // Higher - budget matters for first car
        trust_score: 0.25,               // Higher - want reliable sellers
        listing_quality_score: 0.10,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.10 // Higher - new buyers need help
      }
    },
    {
      id: 'eco_champion',
      name: 'Eco Champion',
      description: 'Low emissions, high efficiency',
      icon: 'Leaf',
      enabled: true,
      filters: {
        fuelType: ['electric', 'hybrid'],
        runningCostBand: ['low']
      },
      rankingWeights: {
        relevance_score: 0.35,           // Higher - specific requirements
        price_fairness_score: 0.15,
        trust_score: 0.15,
        listing_quality_score: 0.15,     // Higher - want detailed specs
        freshness_score: 0.10,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'performance',
      name: 'Performance',
      description: 'Speed and power',
      icon: 'Gauge',
      enabled: true,
      filters: {
        enginePower: { min: 200 },
        bodyType: ['coupe', 'sedan', 'convertible']
      },
      rankingWeights: {
        relevance_score: 0.30,
        price_fairness_score: 0.10,      // Lower - performance buyers less price-sensitive
        trust_score: 0.15,
        listing_quality_score: 0.25,     // Higher - want detailed specs/photos
        freshness_score: 0.10,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    }
  ],
  vans: [
    {
      id: 'business_ready',
      name: 'Business Ready',
      description: 'Perfect for commercial use',
      icon: 'Briefcase',
      enabled: true,
      filters: {
        vatIncluded: true,
        serviceRecords: true,
        verifiedBusiness: true
      },
      countryEnable: ['UK', 'EU'],
      rankingWeights: {
        relevance_score: 0.25,
        price_fairness_score: 0.20,
        trust_score: 0.25,               // Higher - business needs reliability
        listing_quality_score: 0.15,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'high_payload',
      name: 'High Payload',
      description: 'Maximum carrying capacity',
      icon: 'Package',
      enabled: true,
      filters: {
        payloadBand: ['high'],
        cargoVolumeBand: ['large']
      },
      rankingWeights: {
        relevance_score: 0.40,           // Very specific requirement
        price_fairness_score: 0.20,
        trust_score: 0.15,
        listing_quality_score: 0.10,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'delivery_fleet',
      name: 'Delivery Fleet',
      description: 'Ideal for delivery services',
      icon: 'Truck',
      enabled: true,
      filters: {
        fleetMaintained: true,
        runningCostBand: ['low', 'medium']
      },
      rankingWeights: {
        relevance_score: 0.25,
        price_fairness_score: 0.25,      // Cost matters for fleet
        trust_score: 0.20,
        listing_quality_score: 0.10,
        freshness_score: 0.10,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    }
  ],
  buses: [
    {
      id: 'school_transport',
      name: 'School Transport',
      description: 'Safe for student transport',
      icon: 'School',
      enabled: true,
      filters: {
        seats: ['25+'],
        maintenanceRecords: true
      },
      rankingWeights: {
        relevance_score: 0.20,
        price_fairness_score: 0.15,
        trust_score: 0.35,               // Safety is paramount
        listing_quality_score: 0.15,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'tour_ready',
      name: 'Tour Ready',
      description: 'Comfortable for long trips',
      icon: 'Map',
      enabled: true,
      filters: {
        airConditioning: true,
        wheelchairAccessible: true
      },
      rankingWeights: {
        relevance_score: 0.30,
        price_fairness_score: 0.15,
        trust_score: 0.20,
        listing_quality_score: 0.20,     // Comfort details matter
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    }
  ],
  'motorcycles-scooters': [
    {
      id: 'beginner_friendly',
      name: 'Beginner Friendly',
      description: 'Safe and easy to handle',
      icon: 'GraduationCap',
      enabled: true,
      filters: {
        engineCCBand: ['low', 'medium'],
        abs: true
      },
      rankingWeights: {
        relevance_score: 0.25,
        price_fairness_score: 0.25,
        trust_score: 0.25,               // Beginners need trustworthy sellers
        listing_quality_score: 0.10,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'touring',
      name: 'Touring',
      description: 'Built for long rides',
      icon: 'Route',
      enabled: true,
      filters: {
        bikeType: ['touring', 'adventure']
      },
      rankingWeights: {
        relevance_score: 0.35,
        price_fairness_score: 0.15,
        trust_score: 0.15,
        listing_quality_score: 0.20,     // Want detailed specs
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'commuter',
      name: 'Daily Commuter',
      description: 'Fuel efficient for city use',
      icon: 'Building2',
      enabled: true,
      filters: {
        bikeType: ['scooter', 'commuter'],
        engineCCBand: ['low']
      },
      rankingWeights: {
        relevance_score: 0.25,
        price_fairness_score: 0.30,      // Budget matters for daily use
        trust_score: 0.15,
        listing_quality_score: 0.10,
        freshness_score: 0.10,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'sport_rider',
      name: 'Sport Rider',
      description: 'Performance and speed',
      icon: 'Bike',
      enabled: true,
      filters: {
        bikeType: ['sport'],
        engineCCBand: ['high']
      },
      rankingWeights: {
        relevance_score: 0.30,
        price_fairness_score: 0.10,
        trust_score: 0.15,
        listing_quality_score: 0.30,     // Photos and specs very important
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    }
  ],
  'electric-bikes': [
    {
      id: 'long_range',
      name: 'Long Range',
      description: 'Extended battery life',
      icon: 'BatteryFull',
      enabled: true,
      filters: {
        rangeBand: ['high'],
        batteryCapacityWhBand: ['high']
      },
      rankingWeights: {
        relevance_score: 0.40,           // Very specific requirement
        price_fairness_score: 0.15,
        trust_score: 0.15,
        listing_quality_score: 0.15,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'city_commuter',
      name: 'City Commuter',
      description: 'Perfect for urban travel',
      icon: 'Building2',
      enabled: true,
      filters: {
        weightBand: ['light'],
        roadLegal: true
      },
      rankingWeights: {
        relevance_score: 0.30,
        price_fairness_score: 0.25,
        trust_score: 0.15,
        listing_quality_score: 0.10,
        freshness_score: 0.10,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    }
  ],
  'trucks-lorries': [
    {
      id: 'long_haul',
      name: 'Long Haul',
      description: 'Built for distance',
      icon: 'Route',
      enabled: true,
      filters: {
        usageType: ['long_haul'],
        maintenanceRecords: true
      },
      rankingWeights: {
        relevance_score: 0.25,
        price_fairness_score: 0.20,
        trust_score: 0.25,               // Reliability crucial
        listing_quality_score: 0.15,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'local_delivery',
      name: 'Local Delivery',
      description: 'Efficient for short routes',
      icon: 'Package',
      enabled: true,
      filters: {
        grossWeightBand: ['light', 'medium'],
        loadType: ['box']
      },
      rankingWeights: {
        relevance_score: 0.30,
        price_fairness_score: 0.25,
        trust_score: 0.15,
        listing_quality_score: 0.10,
        freshness_score: 0.10,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'construction',
      name: 'Construction Ready',
      description: 'Heavy duty for job sites',
      icon: 'HardHat',
      enabled: true,
      filters: {
        loadType: ['tipper', 'flatbed'],
        grossWeightBand: ['heavy']
      },
      rankingWeights: {
        relevance_score: 0.35,
        price_fairness_score: 0.15,
        trust_score: 0.20,
        listing_quality_score: 0.15,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    }
  ],
  'farm-vehicles': [
    {
      id: 'low_hours',
      name: 'Low Hours',
      description: 'Minimal usage, great condition',
      icon: 'Timer',
      enabled: true,
      filters: {
        operatingHoursBand: ['low']
      },
      rankingWeights: {
        relevance_score: 0.35,
        price_fairness_score: 0.20,
        trust_score: 0.20,
        listing_quality_score: 0.10,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'heavy_duty',
      name: 'Heavy Duty',
      description: 'Maximum power for tough jobs',
      icon: 'Dumbbell',
      enabled: true,
      filters: {
        horsepowerBand: ['high']
      },
      rankingWeights: {
        relevance_score: 0.40,
        price_fairness_score: 0.15,
        trust_score: 0.15,
        listing_quality_score: 0.15,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    }
  ],
  'plant-construction': [
    {
      id: 'ready_to_work',
      name: 'Ready to Work',
      description: 'Well maintained, low hours',
      icon: 'CheckCircle2',
      enabled: true,
      filters: {
        maintenanceRecords: true,
        operatingHoursBand: ['low', 'medium']
      },
      rankingWeights: {
        relevance_score: 0.25,
        price_fairness_score: 0.20,
        trust_score: 0.25,
        listing_quality_score: 0.15,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'excavation',
      name: 'Excavation',
      description: 'Digging equipment',
      icon: 'Shovel',
      enabled: true,
      filters: {
        equipmentType: ['excavator', 'backhoe']
      },
      rankingWeights: {
        relevance_score: 0.40,
        price_fairness_score: 0.15,
        trust_score: 0.15,
        listing_quality_score: 0.15,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    }
  ],
  motorhomes: [
    {
      id: 'family_weekend',
      name: 'Family Weekend',
      description: 'Space for the whole family',
      icon: 'Users',
      enabled: true,
      filters: {
        berths: ['4', '6', '8+'],
        toilet: true
      },
      rankingWeights: {
        relevance_score: 0.25,
        price_fairness_score: 0.15,
        trust_score: 0.25,
        listing_quality_score: 0.20,     // Interior photos important
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'couples_retreat',
      name: 'Couples Retreat',
      description: 'Compact and comfortable',
      icon: 'Heart',
      enabled: true,
      filters: {
        berths: ['2'],
        shower: true
      },
      rankingWeights: {
        relevance_score: 0.30,
        price_fairness_score: 0.20,
        trust_score: 0.15,
        listing_quality_score: 0.20,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    }
  ],
  caravans: [
    {
      id: 'family_tourer',
      name: 'Family Tourer',
      description: 'Space for everyone',
      icon: 'Users',
      enabled: true,
      filters: {
        berths: ['4', '6'],
        toilet: true,
        dampCheck: true
      },
      rankingWeights: {
        relevance_score: 0.25,
        price_fairness_score: 0.15,
        trust_score: 0.30,               // Damp check important
        listing_quality_score: 0.15,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'lightweight',
      name: 'Lightweight',
      description: 'Easy to tow',
      icon: 'Feather',
      enabled: true,
      filters: {
        weightBand: ['light']
      },
      rankingWeights: {
        relevance_score: 0.35,
        price_fairness_score: 0.20,
        trust_score: 0.15,
        listing_quality_score: 0.15,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    }
  ],
  'boats-watercraft': [
    {
      id: 'fishing_ready',
      name: 'Fishing Ready',
      description: 'Perfect for anglers',
      icon: 'Fish',
      enabled: true,
      filters: {
        boatType: ['fishing'],
        trailerIncluded: true,
        surveyAvailable: true
      },
      rankingWeights: {
        relevance_score: 0.30,
        price_fairness_score: 0.15,
        trust_score: 0.25,               // Survey important
        listing_quality_score: 0.15,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'family_cruiser',
      name: 'Family Cruiser',
      description: 'Comfort for day trips',
      icon: 'Sailboat',
      enabled: true,
      filters: {
        passengerCapacityBand: ['medium', 'large'],
        safetyGear: true
      },
      rankingWeights: {
        relevance_score: 0.25,
        price_fairness_score: 0.15,
        trust_score: 0.30,               // Safety crucial
        listing_quality_score: 0.15,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'speed_boat',
      name: 'Speed Boat',
      description: 'Fast and fun',
      icon: 'Waves',
      enabled: true,
      filters: {
        boatType: ['speedboat'],
        horsepowerBand: ['high']
      },
      rankingWeights: {
        relevance_score: 0.30,
        price_fairness_score: 0.10,
        trust_score: 0.15,
        listing_quality_score: 0.30,     // Photos/specs important
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    }
  ],
  'three-wheelers': [
    {
      id: 'taxi_ready_sl',
      name: 'Taxi Ready',
      description: 'Licensed for passenger transport',
      icon: 'Car',
      enabled: true,
      filters: {
        configuration: ['passenger'],
        permitType: ['valid'],
        registrationValid: true
      },
      countryEnable: ['SL'],
      rankingWeights: {
        relevance_score: 0.25,
        price_fairness_score: 0.20,
        trust_score: 0.30,               // Permit/registration crucial
        listing_quality_score: 0.10,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'cargo_carrier',
      name: 'Cargo Carrier',
      description: 'Built for goods transport',
      icon: 'Package',
      enabled: true,
      filters: {
        configuration: ['cargo'],
        cargoSpaceBand: ['large']
      },
      rankingWeights: {
        relevance_score: 0.35,
        price_fairness_score: 0.25,
        trust_score: 0.15,
        listing_quality_score: 0.10,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    },
    {
      id: 'fuel_efficient',
      name: 'Fuel Efficient',
      description: 'Maximum mileage',
      icon: 'Fuel',
      enabled: true,
      filters: {
        fuelType: ['petrol'],
        engineCCBand: ['low']
      },
      rankingWeights: {
        relevance_score: 0.25,
        price_fairness_score: 0.35,      // Running cost is key
        trust_score: 0.15,
        listing_quality_score: 0.10,
        freshness_score: 0.05,
        distance_score: 0.05,
        seller_responsiveness_score: 0.05
      }
    }
  ]
};

// Get presets for a vehicle type (filtered by country and enabled status)
export const getPresetsForVehicle = (vehicleType, country = 'SL') => {
  const presets = AI_PRESETS[vehicleType] || [];
  return presets.filter(preset => {
    // Check if enabled
    if (preset.enabled === false) return false;
    // Check country restrictions
    if (preset.countryEnable && !preset.countryEnable.includes(country)) return false;
    if (preset.countryDisable && preset.countryDisable.includes(country)) return false;
    return true;
  });
};

// Get a specific preset by ID
export const getPresetById = (vehicleType, presetId) => {
  const presets = AI_PRESETS[vehicleType] || [];
  return presets.find(p => p.id === presetId) || null;
};

// Get all presets
export const getAllPresets = () => AI_PRESETS;

// Get preset ranking weights (with fallback to defaults)
export const getPresetRankingWeights = (vehicleType, presetId) => {
  const preset = getPresetById(vehicleType, presetId);
  if (preset && preset.rankingWeights) {
    return preset.rankingWeights;
  }
  return DEFAULT_RANKING_WEIGHTS;
};

export default AI_PRESETS;
